import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Client } from "@xmpp/client"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  selectPlannerCompanies,
  updateSettings,
  type PlannerCompany,
} from "@/reducers/settingsSlice"
import { selectPlannedOrders } from "@/reducers/plannedOrdersSlice"
import { foldCompanyName } from "@/components/timeline/planner/plannerFilters"
import SettingsApi from "@/api/settingsApi"
import ContractorSearch, {
  type Contractor,
} from "@/components/maps/ContractorSearch"
import { Button } from "@/components/ui/button"

// Marcos wstawia w Transport.Customer skrócony ShortName obcięty do 30 znaków,
// a to właśnie ta wartość trafia do metadata.customer_name na zleceniu. Zapis
// czegokolwiek innego sprawiłby, że wybór nie dopasowałby się do żadnego
// zlecenia.
const CUSTOMER_NAME_MAX_LENGTH = 30

const toPlannerCompany = (c: Contractor): PlannerCompany | null => {
  const name = (c.ShortName || c.Name || "")
    .slice(0, CUSTOMER_NAME_MAX_LENGTH)
    .trim()
  if (!name) return null
  return { id: c.Id, tin: c.TIN ?? "", name }
}

export default function MyCompaniesSettings({
  client,
  vhost,
}: {
  client: Client
  vhost: string
}) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const plannerCompanies = useAppSelector(selectPlannerCompanies)
  const plannedOrders = useAppSelector(selectPlannedOrders)
  const [error, setError] = useState<string | null>(null)

  // Ile bieżących zleceń ma każda firma. Pokazujemy LICZBĘ, nie ostrzeżenie o
  // braku dopasowania: planned_delivery_orders to okno ~2 miesięcy, więc u firm
  // sezonowych (targi) zero jest normalnym stanem, a nie awarią — u jednego
  // planisty 20 z 26 firm miało zero i ostrzeżenie przy 77% wierszy przestawało
  // być sygnałem. null = pusty store, czyli jeszcze nie wiemy; wtedy nie
  // pokazujemy niczego zamiast wypisywać wszędzie zero.
  const orderCounts = useMemo(() => {
    if (plannedOrders.length === 0) return null
    const counts = new Map<string, number>()
    for (const order of plannedOrders) {
      const name = order.metadata?.customer_name
      if (typeof name === "string" && name) {
        const key = foldCompanyName(name)
        counts.set(key, (counts.get(key) ?? 0) + 1)
      }
    }
    return counts
  }, [plannedOrders])

  const countFor = (company: PlannerCompany): number | null =>
    orderCounts === null
      ? null
      : (orderCounts.get(foldCompanyName(company.name)) ?? 0)

  // Firmy z ruchem na górze, resztę alfabetycznie — przy dwudziestu kilku
  // pozycjach to, co faktycznie jeździ, musi być widoczne bez przewijania.
  const ordered = useMemo(() => {
    if (orderCounts === null) return plannerCompanies
    return [...plannerCompanies].sort((left, right) => {
      const byCount =
        (orderCounts.get(foldCompanyName(right.name)) ?? 0) -
        (orderCounts.get(foldCompanyName(left.name)) ?? 0)
      return byCount !== 0 ? byCount : left.name.localeCompare(right.name, "pl")
    })
  }, [plannerCompanies, orderCounts])

  const withOrders = useMemo(
    () =>
      orderCounts === null
        ? null
        : plannerCompanies.filter(
            (company) =>
              (orderCounts.get(foldCompanyName(company.name)) ?? 0) > 0,
          ).length,
    [plannerCompanies, orderCounts],
  )

  const save = async (next: PlannerCompany[]) => {
    dispatch(updateSettings({ settings: { plannerCompanies: next } }))
    try {
      await SettingsApi.setPlannerCompanies(client, next)
      setError(null)
    } catch {
      setError(t("myCompanies.saveError"))
    }
  }

  // Klucz to NAZWA, nie id. Po nazwie filtruje Planner, więc dwa wpisy o tej
  // samej nazwie są nierozróżnialne funkcjonalnie. Dodatkowo wpisy zasiane ze
  // starej tabeli mają id 0 dla wszystkich — kluczowanie po id sprawiłoby, że
  // usunięcie jednej zasianej firmy usuwa wszystkie.
  const sameName = (left: string, right: string) =>
    left.trim().toLowerCase() === right.trim().toLowerCase()

  const add = (contractor: Contractor | undefined) => {
    if (!contractor) return
    const company = toPlannerCompany(contractor)
    if (!company) return
    if (
      plannerCompanies.some((existing) => sameName(existing.name, company.name))
    )
      return
    void save([...plannerCompanies, company])
  }

  const remove = (name: string) => {
    void save(
      plannerCompanies.filter((company) => !sameName(company.name, name)),
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="text-sm">{t("myCompanies.intro")}</p>
        <p className="text-xs text-muted-foreground">
          {t("myCompanies.emptyMeansAll")}
        </p>
      </div>

      <ContractorSearch
        client={client}
        vhost={vhost}
        carrier={false}
        limit={50}
        ariaLabel={t("myCompanies.searchLabel")}
        placeholder={t("myCompanies.searchPlaceholder")}
        selected={undefined}
        onSelect={add}
        onError={() => setError(t("myCompanies.fetchError"))}
      />

      {plannerCompanies.length === 0 ? (
        <div className="rounded-md border border-dashed p-4 text-center">
          <p className="text-sm">{t("myCompanies.nothingSelected")}</p>
          <p className="text-xs text-muted-foreground">
            {t("myCompanies.seeAll")}
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-baseline justify-between border-b pb-1">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("myCompanies.selected")}
            </span>
            <span className="text-xs text-muted-foreground">
              {plannerCompanies.length}
              {withOrders !== null &&
                ` · ${t("myCompanies.withOrders", { companies: withOrders })}`}
            </span>
          </div>
          <ul className="-mr-2 max-h-[20rem] divide-y divide-border/50 overflow-y-auto pr-2">
            {ordered.map((company) => {
              const count = countFor(company)
              return (
                <li
                  key={company.name}
                  className="flex items-center gap-2 py-1.5 text-sm"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{company.name}</span>
                    {company.tin && (
                      <span className="block text-xs text-muted-foreground">
                        {t("myCompanies.tin", { tin: company.tin })}
                      </span>
                    )}
                  </span>
                  {count !== null && (
                    <span
                      className={
                        count > 0
                          ? "shrink-0 whitespace-nowrap text-xs text-muted-foreground"
                          : "shrink-0 whitespace-nowrap text-xs text-muted-foreground/50"
                      }
                    >
                      {count > 0
                        ? t("myCompanies.orderCount", { count })
                        : t("myCompanies.noOrders")}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 shrink-0 p-0"
                    aria-label={t("myCompanies.remove", { name: company.name })}
                    onClick={() => remove(company.name)}
                  >
                    ×
                  </Button>
                </li>
              )
            })}
          </ul>
        </>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}
